import LZString from 'lz-string';

/**
 * Utility functions for managing shared document data in localStorage
 * Stores all shared document data as a single JSON object for atomic updates
 * Uses lz-string to compress data for storage.
 *
 * NOTE: We may want to consider encrypting this data before storage,
 * since localStorage is readable by scripts in the browser.
 * However, this currently seems like overkill and we are only compressing it for now.
 * The entry looks like random characters as is right now, so it's not really readable.
 */

const SHARED_DOCUMENT_STORAGE_KEY = 'sharedDocumentData';

/**
 * Get shared document data from localStorage (decompress and parse)
 * @returns {Object|null} Shared document data object or null if not found
 */
export const getSharedDocumentData = () => {
  try {
    const storedJson = localStorage.getItem(SHARED_DOCUMENT_STORAGE_KEY);
    if (!storedJson) return null;
    const parsed = JSON.parse(storedJson);
    const docData = LZString.decompressFromBase64(parsed.token);
    return docData ? JSON.parse(docData) : null;
  } catch (error) {
    console.error(
      'Error reading shared document data from localStorage:',
      error
    );
    return null;
  }
};

/**
 * Set shared document data in localStorage (stringify and compress)
 * @param {Object} data - Shared document data object
 * @param {string} data.documentId - The document ID
 * @param {string} data.orgId - The organization ID
 * @param {string} data.orgName - The organization name
 * @param {string} data.permission - The permission level (viewer, commenter, editor)
 */
export const setSharedDocumentData = ({
  documentId,
  orgId,
  orgName,
  permission,
  grantedViaParentOrg,
  grantedViaEmail,
}) => {
  try {
    const data = {
      documentId,
      orgId,
      orgName,
      permission,
      grantedViaParentOrg,
      grantedViaEmail,
    };
    const json = JSON.stringify(data);
    const compressed = LZString.compressToBase64(json);
    localStorage.setItem(
      SHARED_DOCUMENT_STORAGE_KEY,
      JSON.stringify({token: compressed})
    );
  } catch (error) {
    console.error('Error writing shared document data to localStorage:', error);
  }
};

/**
 * Clear shared document data from localStorage
 */
export const clearSharedDocumentData = () => {
  try {
    localStorage.removeItem(SHARED_DOCUMENT_STORAGE_KEY);
  } catch (error) {
    console.error(
      'Error clearing shared document data from localStorage:',
      error
    );
  }
};

/**
 * Get a specific field from shared document data
 * @param {string} field - The field name to get (documentId, orgId, orgName)
 * @returns {string|null} The field value or null if not found
 */
export const getSharedDocumentField = (field) => {
  const data = getSharedDocumentData();
  return data?.[field] || null;
};
